import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useRenderedMediaUpload } from "@/hooks/useRenderedMediaUpload";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { 
  Share2, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Send, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  RefreshCw,
  Calendar,
  ShoppingBag,
  Heart,
  Sun,
  Gift,
  Mail,
  Settings,
  Plus,
  Eye,
  Trash2,
  Edit,
  Image as ImageIcon,
  FileText,
  Video
} from 'lucide-react';
import { Link } from "react-router-dom";
import logoIcon from "@/assets/regard-de-nature-logo.png";
import { EntityType, DraftStatus, SocialChannels, CopyContent, ENTITY_TYPE_LABELS, getShareUrl } from "@/types/socialPublishing";
import { BrandedPreview } from "@/components/social/BrandedPreview";
import { VideoReelsDrafts } from "@/components/social/VideoReelsDrafts";

// Types locaux pour les drafts
// Hashtags prédéfinis naturopathie
const NATUROPATHY_HASHTAGS = [
  '#naturopathie', '#bienetre', '#sante', '#naturel', '#iridologie',
  '#phytotherapie', '#aromatherapie', '#gemmotherapie', '#hygienevitale',
  '#santeholistique', '#medecinenatturelle', '#fleursdebach', '#equilibre',
  '#vitalite', '#terrainbiologique', '#preventionsante', '#naturopathe',
  '#bienetrenaturel', '#conseilssante', '#hygienedevie'
];

interface DraftItem {
  id: string;
  title: string;
  entity_type: EntityType;
  entity_id: string | null;
  status: DraftStatus;
  channels: SocialChannels;
  copy: CopyContent;
  media: any;
  render_mode: 'legacy_image' | 'screenshot_share';
  share_url: string | null;
  schedule: { publish_at: string | null; timezone: string };
  published_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  // Branding fields
  visual_title: string | null;
  visual_title_size: number;
  show_logo: boolean;
  show_site_name: boolean;
}

interface ContentEntity {
  id: string;
  title: string;
  description?: string;
  image_url?: string;
  is_active: boolean;
  slug?: string;
}

interface GlobalSettings {
  make_webhook_social_url: string | null;
  social_publish_enabled: boolean;
  social_publish_mode: 'test' | 'production';
}

function parseEdgeFunctionError(err: any): { code?: string; message: string } {
  // Supabase Functions errors often look like:
  // { message: "Edge Function returned a non-2xx status code", context: { status: 400, body: "{...}" } }
  const generic = typeof err?.message === 'string' ? err.message : 'Erreur inconnue';

  const body = err?.context?.body;
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      const code = parsed?.error || parsed?.code;
      const message = parsed?.message || parsed?.error_description || generic;
      return { code, message };
    } catch {
      return { message: body || generic };
    }
  }

  if (body && typeof body === 'object') {
    const code = (body as any)?.error || (body as any)?.code;
    const message = (body as any)?.message || generic;
    return { code, message };
  }

  return { message: generic };
}

export default function SocialPublishing() {
  const [settings, setSettings] = useState<GlobalSettings | null>(null);
  const [drafts, setDrafts] = useState<DraftItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<EntityType>('accompagnement');
  
  // Modal d'édition/création
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentDraft, setCurrentDraft] = useState<Partial<DraftItem> | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Contenus disponibles pour création
  const [availableContent, setAvailableContent] = useState<ContentEntity[]>([]);
  
  // Preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDraft, setPreviewDraft] = useState<DraftItem | null>(null);
  
  // Hook pour le rendu des visuels brandés
  const { renderAndUploadMedia, isRendering } = useRenderedMediaUpload();
  
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Charger les paramètres
      const { data: settingsData } = await supabase
        .from('app_settings')
        .select('make_webhook_social_url, social_publish_enabled, social_publish_mode')
        .limit(1)
        .single();
      
      setSettings(settingsData as GlobalSettings);

      // Charger les brouillons de publication
      const { data: draftsData, error: draftsError } = await supabase
        .from('social_publication_drafts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (draftsError) {
        console.error('Error loading drafts:', draftsError);
      } else {
        const rawDrafts = (draftsData || []) as unknown as DraftItem[];

        // Normalisation défensive : si published_at existe, le statut doit être 'published'
        const toFix = rawDrafts
          .filter(d => !!d.published_at && d.status !== 'published')
          .map(d => d.id);

        if (toFix.length > 0) {
          console.warn('[SocialPublishing] Fixing inconsistent drafts (published_at set but status != published):', toFix);
          await supabase
            .from('social_publication_drafts')
            .update({ status: 'published', last_error: null })
            .in('id', toFix);
        }

        const normalizedDrafts = rawDrafts.map(d =>
          d.published_at
            ? ({ ...d, status: 'published' as DraftStatus, last_error: null } as DraftItem)
            : d
        );

        setDrafts(normalizedDrafts);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Charger le contenu disponible quand on change d'onglet
  useEffect(() => {
    if (activeTab && activeTab !== 'manuel') {
      loadAvailableContent(activeTab);
    } else {
      setAvailableContent([]);
    }
  }, [activeTab]);

  const loadAvailableContent = async (entityType: EntityType) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || entityType === 'manuel') return;

    try {
      let data: any[] | null = null;
      
      if (entityType === 'accompagnement') {
        const res = await supabase.from('accompaniments').select('*').eq('user_id', user.id).eq('is_active', true);
        data = res.data;
      } else if (entityType === 'evenement') {
        const res = await supabase.from('events').select('*').eq('user_id', user.id).eq('is_active', true);
        data = res.data;
      } else if (entityType === 'boutique') {
        const res = await supabase.from('products').select('*').eq('user_id', user.id).eq('is_active', true);
        data = res.data;
      } else if (entityType === 'bien-etre-saisonnier') {
        const res = await supabase.from('seasonal_wellness').select('*').eq('user_id', user.id).eq('is_active', true);
        data = res.data;
      } else if (entityType === 'cartes-cadeaux') {
        const res = await supabase.from('gift_card_settings').select('*').eq('user_id', user.id);
        data = res.data;
      }
      
      if (data) {
        const mapped = data.map((item: any) => ({
          id: item.id,
          title: item.title || item.name || item.season || 'Carte cadeau',
          description: item.short_description || item.description,
          image_url: item.image_url || item.cover_image_url,
          is_active: item.is_active !== false,
          slug: item.slug || item.id,
        }));
        setAvailableContent(mapped);
      }
    } catch (e) {
      console.error('Error loading content:', e);
    }
  };

  // Filtrer les drafts par type
  const filteredDrafts = useMemo(() => {
    return drafts.filter(d => d.entity_type === activeTab);
  }, [drafts, activeTab]);

  const openCreateModal = (entityId?: string) => {
    const entity = entityId ? availableContent.find(c => c.id === entityId) : null;
    
    const newDraft: Partial<DraftItem> = {
      entity_type: activeTab,
      entity_id: entityId || null,
      title: entity?.title || '',
      status: 'pending',
      channels: {
        instagram_post: false,
        instagram_story: false,
        instagram_reel: false,
        facebook_page: false,
        facebook_story: false,
        facebook_reel: false,
        linkedin_page: false,
        linkedin_reel: false,
        tiktok: false,
        newsletter: false,
      },
      copy: {
        default: '',
        instagram: { caption: null, hashtags: null, alt_text: null },
        facebook: { message: null },
        linkedin: { text: null },
        story: { text: null, cta: { label: null, url: null } },
        reel: { caption: null },
        email: { subject: null, preheader: null },
      },
      media: {
        mode: 'image',
        cover: { url: entity?.image_url || null, type: 'image', width: 1080, height: 1080 },
        carousel: { items: [] },
        video: { url: null, type: null, duration_seconds: null, poster_url: null, width: null, height: null },
        story: { image_url: entity?.image_url || null, video_url: null, type: 'image', width: 1080, height: 1920 },
        reel: { video_url: null, type: null, duration_seconds: null, poster_url: null, width: 1080, height: 1920 },
      },
      render_mode: 'legacy_image',
      share_url: entity?.slug && activeTab !== 'manuel' ? getShareUrl(activeTab, entity.slug) : null,
      schedule: { publish_at: null, timezone: 'Europe/Paris' },
      // Branding defaults
      visual_title: entity?.title || null,
      show_logo: true,
      show_site_name: true,
    };
    
    setCurrentDraft(newDraft);
    setIsCreating(true);
    setEditModalOpen(true);
  };

  const openEditModal = (draft: DraftItem) => {
    setCurrentDraft(draft);
    setIsCreating(false);
    setEditModalOpen(true);
  };

  const saveDraft = async () => {
    if (!currentDraft) return;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      if (isCreating) {
        // Créer un nouveau brouillon
        const { data, error } = await supabase
          .from('social_publication_drafts')
          .insert({
            user_id: user.id,
            entity_type: currentDraft.entity_type!,
            entity_id: currentDraft.entity_id,
            title: currentDraft.title!,
            status: 'pending',
            channels: currentDraft.channels as any,
            copy: currentDraft.copy as any,
            media: currentDraft.media as any,
            render_mode: currentDraft.render_mode!,
            share_url: currentDraft.share_url,
            schedule: currentDraft.schedule as any,
            visual_title: currentDraft.visual_title,
            show_logo: currentDraft.show_logo ?? true,
            show_site_name: currentDraft.show_site_name ?? true,
          })
          .select()
          .single();

        if (error) throw error;
        setDrafts([data as unknown as DraftItem, ...drafts]);
        toast({ title: "Brouillon créé", description: "Votre brouillon a été enregistré" });
      } else {
        // Mettre à jour un brouillon existant
        const { error } = await supabase
          .from('social_publication_drafts')
          .update({
            title: currentDraft.title,
            channels: currentDraft.channels as any,
            copy: currentDraft.copy as any,
            media: currentDraft.media as any,
            render_mode: currentDraft.render_mode,
            share_url: currentDraft.share_url,
            schedule: currentDraft.schedule as any,
            status: 'ready',
            visual_title: currentDraft.visual_title,
            show_logo: currentDraft.show_logo ?? true,
            show_site_name: currentDraft.show_site_name ?? true,
          })
          .eq('id', currentDraft.id!);

        if (error) throw error;
        setDrafts(drafts.map(d => d.id === currentDraft.id ? { ...d, ...currentDraft, status: 'ready' } as DraftItem : d));
        toast({ title: "Brouillon mis à jour" });
      }

      setEditModalOpen(false);
      setCurrentDraft(null);
    } catch (error: any) {
      console.error('Save error:', error);
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    }
  };

  const deleteDraft = async (id: string) => {
    if (!confirm('Supprimer ce brouillon ?')) return;

    const { error } = await supabase
      .from('social_publication_drafts')
      .delete()
      .eq('id', id);

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      setDrafts(drafts.filter(d => d.id !== id));
      toast({ title: "Brouillon supprimé" });
    }
  };

  const handlePreview = (draft: DraftItem) => {
    setPreviewDraft(draft);
    setPreviewOpen(true);
  };

  const handlePublish = async (draft: DraftItem) => {
    // ===== VALIDATION 0: Déjà publié (published_at fait foi) =====
    if (draft.published_at) {
      const publishedAtLabel = new Date(draft.published_at).toLocaleString('fr-FR');

      // Réparer immédiatement l'incohérence éventuelle (status=error mais published_at non-null)
      if (draft.status !== 'published') {
        await supabase
          .from('social_publication_drafts')
          .update({ status: 'published', last_error: null })
          .eq('id', draft.id);

        setDrafts(drafts.map(d =>
          d.id === draft.id ? ({ ...d, status: 'published', last_error: null } as DraftItem) : d
        ));
      }

      toast({
        title: "Déjà publié",
        description: `Ce brouillon a déjà été publié le ${publishedAtLabel}. Pour republier, créez un nouveau brouillon.`,
      });
      return;
    }

    // ===== VALIDATION 1: Mode Production obligatoire =====
    if (settings?.social_publish_mode !== 'production') {
      toast({
        title: "Mode Production requis",
        description: "La publication réseaux sociaux n'est autorisée qu'en mode Production. Changez le mode dans Paramètres.",
        variant: "destructive",
      });
      console.log('[Publish] BLOCKED: Mode is not production, current mode:', settings?.social_publish_mode);
      return;
    }

    // ===== VALIDATION 2: Webhook configuré =====
    if (!settings?.make_webhook_social_url) {
      toast({
        title: "Configuration manquante",
        description: "Configurez l'URL du webhook Make.com dans Paramètres",
        variant: "destructive",
      });
      return;
    }

    // ===== VALIDATION 3: Au moins un canal sélectionné =====
    const hasChannel = Object.values(draft.channels).some(v => v === true);
    if (!hasChannel) {
      toast({
        title: "Aucun canal sélectionné",
        description: "Cochez au moins un canal de publication",
        variant: "destructive",
      });
      return;
    }

    setPublishingId(draft.id);
    
    console.log('========================================');
    console.log('[Publish] START - Draft:', draft.id);
    console.log('[Publish] Mode: PRODUCTION (validated)');
    console.log('========================================');
    
    try {
      // ===== ÉTAPE 1: Génération des visuels brandés =====
      // OBLIGATOIRE: Toujours générer et uploader les images
      toast({
        title: "Génération des visuels...",
        description: "Création des images feed (1080x1080) et story (1080x1920)",
      });

      console.log('[Publish] STEP 1: render:start');
      console.log('[Publish] Render options:', {
        coverImageUrl: draft.media?.cover?.url || null,
        visualTitle: draft.visual_title,
        showLogo: draft.show_logo,
        showSiteName: draft.show_site_name,
        draftId: draft.id,
      });

      const renderedMedia = await renderAndUploadMedia({
        coverImageUrl: draft.media?.cover?.url || null,
        visualTitle: draft.visual_title,
        showLogo: draft.show_logo,
        showSiteName: draft.show_site_name,
        draftId: draft.id,
      });

      console.log('[Publish] STEP 1: render:done');
      console.log('[Publish] feed_url:', renderedMedia.feed_url || 'NULL');
      console.log('[Publish] story_url:', renderedMedia.story_url || 'NULL');

      // Vérifier que les URLs ont été générées
      if (!renderedMedia.feed_url || !renderedMedia.story_url) {
        console.error('[Publish] CRITICAL: Rendered URLs are null!');
        throw new Error('Échec de la génération des visuels. Vérifiez la console pour plus de détails.');
      }

      console.log('[Publish] STEP 1: upload:done - Both URLs generated successfully');

      // Mettre à jour le draft avec les URLs rendues (pour historique)
      const updatedMedia = {
        ...draft.media,
        rendered: {
          feed_url: renderedMedia.feed_url,
          story_url: renderedMedia.story_url,
        },
      };

      await supabase
        .from('social_publication_drafts')
        .update({ media: updatedMedia as any })
        .eq('id', draft.id);

      console.log('[Publish] Rendered URLs saved to DB');

      // ===== ÉTAPE 2: Envoi vers Make.com =====
      console.log('[Publish] STEP 2: make:post - Calling Edge Function');
      console.log('[Publish] rendered_media payload:', {
        feed_url: renderedMedia.feed_url,
        story_url: renderedMedia.story_url,
      });

      const { data, error } = await supabase.functions.invoke('social-push-to-make', {
        body: { 
          action: 'publish_draft',
          draft_id: draft.id,
          rendered_media: {
            feed_url: renderedMedia.feed_url,
            story_url: renderedMedia.story_url,
          },
        },
      });
      
      console.log('[Publish] STEP 2: make:post - Response received');
      console.log('[Publish] Response data:', JSON.stringify(data));
      
      if (error) {
        console.error('[Publish] Edge Function error:', error);
        throw error;
      }

      // Mettre à jour le statut local
      setDrafts(drafts.map(d => 
        d.id === draft.id 
          ? { ...d, status: 'published' as DraftStatus, published_at: new Date().toISOString(), media: updatedMedia } 
          : d
      ));

      console.log('========================================');
      console.log('[Publish] SUCCESS - Publication complete');
      console.log('========================================');

      toast({
        title: "Publication réussie !",
        description: "Les visuels ont été générés et envoyés vers Make.com",
      });
      
    } catch (error: any) {
      const parsed = parseEdgeFunctionError(error);

      console.error('========================================');
      console.error('[Publish] ERROR:', parsed.message);
      console.error('Raw error:', error);
      console.error('========================================');

      // Cas fréquent: le brouillon est en réalité déjà publié (published_at set côté DB)
      // → on évite de polluer le statut en "error".
      if (parsed.code === 'ALREADY_PUBLISHED') {
        toast({
          title: "Déjà publié",
          description: parsed.message,
        });
        return;
      }

      // Marquer en erreur
      await supabase
        .from('social_publication_drafts')
        .update({ status: 'error', last_error: parsed.message })
        .eq('id', draft.id);

      setDrafts(drafts.map(d =>
        d.id === draft.id
          ? ({ ...d, status: 'error' as DraftStatus, last_error: parsed.message } as DraftItem)
          : d
      ));

      toast({
        title: "Erreur de publication",
        description: parsed.message,
        variant: "destructive",
      });
    } finally {
      setPublishingId(null);
    }
  };

  const getStatusBadge = (status: DraftStatus) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Publié</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Erreur</Badge>;
      case 'ready':
        return <Badge className="bg-blue-100 text-blue-700"><CheckCircle2 className="w-3 h-3 mr-1" />Prêt</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-600"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
    }
  };

  const getEntityIcon = (type: EntityType) => {
    switch (type) {
      case 'accompagnement': return <Heart className="w-4 h-4" />;
      case 'evenement': return <Calendar className="w-4 h-4" />;
      case 'boutique': return <ShoppingBag className="w-4 h-4" />;
      case 'bien-etre-saisonnier': return <Sun className="w-4 h-4" />;
      case 'cartes-cadeaux': return <Gift className="w-4 h-4" />;
      case 'manuel': return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageContainer>
    );
  }

  const webhookConfigured = !!settings?.make_webhook_social_url;

  return (
    <PageContainer>
      <PageHeader
        title="Publication Réseaux Sociaux"
        subtitle="Préparez, prévisualisez et publiez vos contenus sur les réseaux sociaux"
        icon={<Share2 className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="secondary"
        actions={
          <div className="flex items-center gap-2">
            {settings?.social_publish_enabled && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                Mode: {settings.social_publish_mode?.toUpperCase()}
              </Badge>
            )}
            <Link to="/account-settings">
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Paramètres
              </Button>
            </Link>
          </div>
        }
      />

      <div className="space-y-6">
        {/* Alerte si non configuré */}
        {!webhookConfigured && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div className="flex-1">
                  <p className="font-medium text-amber-800">Configuration requise</p>
                  <p className="text-sm text-amber-700">
                    Configurez l'URL du webhook Make.com dans les paramètres pour activer la publication.
                  </p>
                </div>
                <Link to="/account-settings">
                  <Button size="sm" variant="outline" className="border-amber-300 text-amber-700">
                    Configurer
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs de haut niveau: Posts vs Vidéos */}
        <Tabs defaultValue="posts" className="space-y-6">
          <TabsList className="h-auto w-full grid grid-cols-2">
            <TabsTrigger value="posts" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <ImageIcon className="w-3 h-3 sm:w-4 sm:h-4" /> 
              <span className="hidden xs:inline">Posts /</span> Images
            </TabsTrigger>
            <TabsTrigger value="videos" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <Video className="w-3 h-3 sm:w-4 sm:h-4" /> 
              <span className="hidden xs:inline">Vidéos</span> Reels
            </TabsTrigger>
          </TabsList>

          <TabsContent value="posts">
            {/* Tabs par type de contenu - mobile horizontal scroll */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as EntityType)}>
              <div className="overflow-x-auto -mx-4 px-4 pb-2 sm:mx-0 sm:px-0">
                <TabsList className="inline-flex w-max sm:flex sm:flex-wrap h-auto gap-1 p-1">
                  <TabsTrigger value="accompagnement" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <Heart className="w-3 h-3 sm:w-4 sm:h-4" /> 
                    <span className="hidden xs:inline">Accompagne</span>
                    <span className="xs:hidden">Acc.</span>
                  </TabsTrigger>
                  <TabsTrigger value="evenement" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" /> Events
                  </TabsTrigger>
                  <TabsTrigger value="boutique" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <ShoppingBag className="w-3 h-3 sm:w-4 sm:h-4" /> Boutique
                  </TabsTrigger>
                  <TabsTrigger value="bien-etre-saisonnier" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <Sun className="w-3 h-3 sm:w-4 sm:h-4" /> Saisonnier
                  </TabsTrigger>
                  <TabsTrigger value="cartes-cadeaux" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <Gift className="w-3 h-3 sm:w-4 sm:h-4" /> 
                    <span className="hidden xs:inline">Cartes</span> Cadeaux
                  </TabsTrigger>
                  <TabsTrigger value="manuel" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3 py-1.5 whitespace-nowrap flex-shrink-0">
                    <FileText className="w-3 h-3 sm:w-4 sm:h-4" /> Manuel
                  </TabsTrigger>
                </TabsList>
              </div>

          <div className="mt-4 sm:mt-6">
            {/* Bouton créer - mobile responsive */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-base sm:text-lg font-semibold flex items-center gap-2">
                {getEntityIcon(activeTab)}
                <span className="truncate">{ENTITY_TYPE_LABELS[activeTab]}</span>
                <Badge variant="secondary" className="flex-shrink-0">{filteredDrafts.length}</Badge>
              </h2>
              
              {activeTab === 'manuel' ? (
                <Button onClick={() => openCreateModal()} size="sm" className="w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau post
                </Button>
              ) : availableContent.length > 0 ? (
                <Select onValueChange={(v) => openCreateModal(v)}>
                  <SelectTrigger className="w-full sm:w-[280px]">
                    <SelectValue placeholder="Créer depuis..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableContent.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs sm:text-sm text-muted-foreground">Aucun contenu disponible</p>
              )}
            </div>

            {/* Liste des brouillons */}
            {filteredDrafts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Share2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucun brouillon pour cette catégorie</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {activeTab === 'manuel' 
                      ? "Créez un post manuel indépendant"
                      : "Sélectionnez un contenu actif pour créer un brouillon"
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredDrafts.map((draft) => (
                  <Card key={draft.id} className={draft.status === 'error' ? 'border-red-200' : ''}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base line-clamp-1">{draft.title}</CardTitle>
                        {getStatusBadge(draft.status)}
                      </div>
                      {draft.last_error && (
                        <p className="text-xs text-red-600 mt-1">{draft.last_error}</p>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Canaux sélectionnés */}
                      <div className="flex flex-wrap gap-1">
                        {draft.channels.instagram_post && <Badge variant="secondary" className="text-xs"><Instagram className="w-3 h-3 mr-1" />Post</Badge>}
                        {draft.channels.instagram_story && <Badge variant="secondary" className="text-xs"><Instagram className="w-3 h-3 mr-1" />Story</Badge>}
                        {draft.channels.instagram_reel && <Badge variant="secondary" className="text-xs"><Instagram className="w-3 h-3 mr-1" />Reel</Badge>}
                        {draft.channels.facebook_page && <Badge variant="secondary" className="text-xs"><Facebook className="w-3 h-3 mr-1" />Post</Badge>}
                        {draft.channels.facebook_story && <Badge variant="secondary" className="text-xs"><Facebook className="w-3 h-3 mr-1" />Story</Badge>}
                        {draft.channels.linkedin_page && <Badge variant="secondary" className="text-xs"><Linkedin className="w-3 h-3 mr-1" />Post</Badge>}
                        {draft.channels.newsletter && <Badge variant="secondary" className="text-xs"><Mail className="w-3 h-3 mr-1" />Email</Badge>}
                      </div>

                      {/* Branding indicators */}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {draft.show_logo && <span>✓ Logo</span>}
                        {draft.show_site_name && <span>✓ Nom</span>}
                        {draft.visual_title && <span>✓ Titre visuel</span>}
                      </div>

                      {/* Texte par défaut */}
                      {draft.copy.default && (
                        <p className="text-xs text-muted-foreground line-clamp-2">{draft.copy.default}</p>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(draft)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handlePreview(draft)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteDraft(draft.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handlePublish(draft)}
                          disabled={publishingId === draft.id || draft.status === 'published' || !!draft.published_at}
                        >
                          {publishingId === draft.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (draft.status === 'published' || draft.published_at) ? (
                            <>
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Publié
                            </>
                          ) : draft.status === 'error' ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Relancer
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              Publier
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Tabs>
          </TabsContent>

          <TabsContent value="videos">
            <VideoReelsDrafts settings={settings} />
          </TabsContent>
        </Tabs>

        {/* Modal d'édition */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {isCreating ? 'Nouveau brouillon de publication' : 'Modifier le brouillon'}
              </DialogTitle>
              <DialogDescription>
                Configurez les canaux, textes, médias et branding pour cette publication
              </DialogDescription>
            </DialogHeader>

            {currentDraft && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Colonne gauche : Formulaire */}
                <div className="space-y-6">
                  {/* Titre interne */}
                  <div className="space-y-2">
                    <Label>Titre interne</Label>
                    <Input
                      value={currentDraft.title || ''}
                      onChange={(e) => setCurrentDraft({ ...currentDraft, title: e.target.value })}
                      placeholder="Titre pour identifier cette publication"
                    />
                  </div>

                  <Separator />

                  {/* Branding Section */}
                  <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      Branding du visuel
                    </h4>
                    
                    {/* Titre visuel */}
                    <div className="space-y-2">
                      <Label>Titre visuel (optionnel, 1 ligne max)</Label>
                      <Input
                        value={currentDraft.visual_title || ''}
                        onChange={(e) => setCurrentDraft({ ...currentDraft, visual_title: e.target.value })}
                        placeholder="Titre affiché sur l'image"
                        maxLength={60}
                      />
                    </div>

                    {/* Taille du titre visuel */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Taille du titre</Label>
                        <span className="text-xs text-muted-foreground">{currentDraft.visual_title_size || 48}px</span>
                      </div>
                      <input
                        type="range"
                        min="24"
                        max="72"
                        step="2"
                        value={currentDraft.visual_title_size || 48}
                        onChange={(e) => setCurrentDraft({ ...currentDraft, visual_title_size: parseInt(e.target.value) })}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Petit</span>
                        <span>Grand</span>
                      </div>
                    </div>

                    {/* Toggles branding */}
                    <div className="flex items-center justify-between">
                      <Label htmlFor="show-logo" className="cursor-pointer">Afficher le logo</Label>
                      <Switch
                        id="show-logo"
                        checked={currentDraft.show_logo ?? true}
                        onCheckedChange={(checked) => setCurrentDraft({ ...currentDraft, show_logo: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label htmlFor="show-site-name" className="cursor-pointer">Afficher le nom du site</Label>
                      <Switch
                        id="show-site-name"
                        checked={currentDraft.show_site_name ?? true}
                        onCheckedChange={(checked) => setCurrentDraft({ ...currentDraft, show_site_name: checked })}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Canaux */}
                  <div className="space-y-3">
                    <Label className="text-base font-semibold">Canaux de publication</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {(['instagram_post', 'instagram_story', 'instagram_reel', 'facebook_page', 'facebook_story', 'linkedin_page'] as const).map((channel) => (
                        <label key={channel} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-muted">
                          <Checkbox
                            checked={(currentDraft.channels as any)?.[channel] || false}
                            onCheckedChange={(checked) => 
                              setCurrentDraft({
                                ...currentDraft,
                                channels: { ...currentDraft.channels as SocialChannels, [channel]: !!checked }
                              })
                            }
                          />
                          {channel.includes('instagram') && <Instagram className="w-4 h-4 text-pink-600" />}
                          {channel.includes('facebook') && <Facebook className="w-4 h-4 text-blue-600" />}
                          {channel.includes('linkedin') && <Linkedin className="w-4 h-4 text-blue-700" />}
                          <span className="text-sm capitalize">{channel.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Texte par défaut */}
                  <div className="space-y-2">
                    <Label>Texte par défaut</Label>
                    <Textarea
                      value={(currentDraft.copy as CopyContent)?.default || ''}
                      onChange={(e) => setCurrentDraft({
                        ...currentDraft,
                        copy: { ...currentDraft.copy as CopyContent, default: e.target.value }
                      })}
                      rows={4}
                      placeholder="Texte de la publication..."
                    />
                  </div>

                  {/* Hashtags Instagram */}
                  <div className="space-y-2">
                    <Label>Hashtags Instagram</Label>
                    <Input
                      value={(currentDraft.copy as CopyContent)?.instagram?.hashtags || ''}
                      onChange={(e) => setCurrentDraft({
                        ...currentDraft,
                        copy: { 
                          ...currentDraft.copy as CopyContent, 
                          instagram: { 
                            ...((currentDraft.copy as CopyContent)?.instagram || { caption: null, alt_text: null }),
                            hashtags: e.target.value 
                          }
                        }
                      })}
                      placeholder="#naturopathie #bienetre #sante..."
                    />
                    {/* Hashtags cliquables */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {NATUROPATHY_HASHTAGS.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          className="px-2 py-0.5 text-xs bg-muted hover:bg-primary/10 text-muted-foreground hover:text-primary rounded-full border transition-colors"
                          onClick={() => {
                            const current = (currentDraft.copy as CopyContent)?.instagram?.hashtags || '';
                            if (!current.includes(tag)) {
                              setCurrentDraft({
                                ...currentDraft,
                                copy: { 
                                  ...currentDraft.copy as CopyContent, 
                                  instagram: { 
                                    ...((currentDraft.copy as CopyContent)?.instagram || { caption: null, alt_text: null }),
                                    hashtags: current ? `${current} ${tag}` : tag
                                  }
                                }
                              });
                            }
                          }}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Média */}
                  <div className="space-y-2">
                    <Label>Image de couverture (URL)</Label>
                    <Input
                      value={(currentDraft.media as any)?.cover?.url || ''}
                      onChange={(e) => setCurrentDraft({
                        ...currentDraft,
                        media: { 
                          ...currentDraft.media,
                          cover: { 
                            ...((currentDraft.media as any)?.cover || {}),
                            url: e.target.value,
                            type: 'image',
                            width: 1080,
                            height: 1080
                          }
                        }
                      })}
                      placeholder="https://..."
                    />
                  </div>

                  {/* Mode rendu (pour contenus liés uniquement) */}
                  {currentDraft.entity_type !== 'manuel' && (
                    <div className="space-y-2">
                      <Label>Mode de rendu visuel</Label>
                      <Select
                        value={currentDraft.render_mode}
                        onValueChange={(v) => setCurrentDraft({ ...currentDraft, render_mode: v as 'legacy_image' | 'screenshot_share' })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="legacy_image">
                            <div className="flex items-center gap-2">
                              <ImageIcon className="w-4 h-4" />
                              Image directe (legacy)
                            </div>
                          </SelectItem>
                          <SelectItem value="screenshot_share">
                            <div className="flex items-center gap-2">
                              <Eye className="w-4 h-4" />
                              Capture page /share
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Colonne droite : Aperçu en temps réel */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm">Aperçu en temps réel</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Feed Preview */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Feed (1:1)</p>
                      <BrandedPreview
                        format="feed"
                        coverImageUrl={(currentDraft.media as any)?.cover?.url || null}
                        visualTitle={currentDraft.visual_title || null}
                        visualTitleSize={currentDraft.visual_title_size || 48}
                        showLogo={currentDraft.show_logo ?? true}
                        showSiteName={currentDraft.show_site_name ?? true}
                      />
                    </div>
                    
                    {/* Story Preview */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Story (9:16)</p>
                      <BrandedPreview
                        format="story"
                        coverImageUrl={(currentDraft.media as any)?.story?.image_url || (currentDraft.media as any)?.cover?.url || null}
                        visualTitle={currentDraft.visual_title || null}
                        visualTitleSize={currentDraft.visual_title_size || 48}
                        showLogo={currentDraft.show_logo ?? true}
                        showSiteName={currentDraft.show_site_name ?? true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                Annuler
              </Button>
              <Button onClick={saveDraft}>
                {isCreating ? 'Créer le brouillon' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Preview complet */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Aperçu de la publication</DialogTitle>
              <DialogDescription>
                Vérifiez le rendu avant de publier
              </DialogDescription>
            </DialogHeader>
            
            {previewDraft && (
              <div className="grid md:grid-cols-2 gap-8">
                {/* Preview Feed (1080x1080) */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Format Feed (1:1)</Label>
                  <BrandedPreview
                    format="feed"
                    coverImageUrl={(previewDraft.media as any)?.cover?.url || null}
                    visualTitle={previewDraft.visual_title || null}
                    visualTitleSize={previewDraft.visual_title_size || 48}
                    showLogo={previewDraft.show_logo ?? true}
                    showSiteName={previewDraft.show_site_name ?? true}
                    text={(previewDraft.copy as CopyContent)?.default || ''}
                    hashtags={(previewDraft.copy as CopyContent)?.instagram?.hashtags}
                  />
                </div>

                {/* Preview Story (1080x1920) */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Format Story (9:16)</Label>
                  <BrandedPreview
                    format="story"
                    coverImageUrl={(previewDraft.media as any)?.story?.image_url || (previewDraft.media as any)?.cover?.url || null}
                    visualTitle={previewDraft.visual_title || null}
                    visualTitleSize={previewDraft.visual_title_size || 48}
                    showLogo={previewDraft.show_logo ?? true}
                    showSiteName={previewDraft.show_site_name ?? true}
                    text={(previewDraft.copy as CopyContent)?.story?.text || (previewDraft.copy as CopyContent)?.default || ''}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                Fermer
              </Button>
              {previewDraft && previewDraft.status !== 'published' && !previewDraft.published_at && (
                <Button onClick={() => {
                  setPreviewOpen(false);
                  handlePublish(previewDraft);
                }}>
                  <Send className="w-4 h-4 mr-2" />
                  Publier maintenant
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageContainer>
  );
}
